import { useAuth, WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { useTranslation } from "react-i18next";
import { Box, Button, Center, chakra, Flex, Heading, Tag, useToast } from "@chakra-ui/react";
import { FormInput, FormSelect } from "../../components/Forms";
import { useForm } from "react-hook-form";
import { LocationQuestionnaire } from "./components";
import {
  supplierPhysicalRiskQuestionnaireKeys,
  useCreateNewLocation,
  useGetSupplierPhysicalRiskQuestionnaireById,
} from "../../hooks/query/supplierPhysicalRiskQuestionnaire";
import { QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast, SUPPLIER_TYPES } from "../../utils";
import { isAllowedToView } from "utils/AuthUtils";
import { useEffect, useState } from "react";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { NavigationTabs } from "../../components/NavigationTabs";
import { mapLocationsModalData } from "../PhysicalRisk/helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { ReturnToList } from "../SupplierQuestionnaire/components/ReturnToList";
import * as React from "react";
import { AssigneeHeader } from "../../components/AssigneeHeader";
import { getSupplierQuestionnairesRoute } from "../SupplierQuestionnaires/helpers";
import { routes } from "../../config";

function SupplierPhysicalRiskQuestionnaire() {
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierPhysicalRiskQuestionnaire",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { userPermissions } = useAuth();
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const assignee = {
    name: user.name,
    avatarUrl: user.picture,
  };

  const [submitAndReturnToList, setSubmitAndReturnToList] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({});

  const { data, isLoading, isFetching, error } = useGetSupplierPhysicalRiskQuestionnaireById(
    location.search.split("id=")[1] ?? null,
    {
      enabled:
        location &&
        location.search !== "" &&
        !isNaN(location.search.split("id=")[1]) &&
        location.search.split("id=")[1] !== null,
      onError: (err) => (err.response?.status !== 404 ? showErrorToast(toast, err.message) : null),
      onSettled: () => dispatch(hideAppSpinner()),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const isPreview =
    (data?.assigneeName?.toLowerCase() !== user.email?.toLowerCase() &&
      !isAllowedToView(userPermissions, ["ORG_ADMIN"])) ||
    (userPermissions?.length === 1 && userPermissions?.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER));

  const executeNavigation = () => {
    if (assignee?.name?.toLowerCase() === data?.assigneeName?.toLowerCase() || isPreview) {
      // The assignee of the questionnaire is editing it or colleague of the assignee is reviewing it, so we are
      // returning to 'Supplier Questionnaires' page
      navigate(getSupplierQuestionnairesRoute(userPermissions));
    } else {
      // Admin is editing someone else's questionnaire, so we are returning to 'Data entry planning' page
      navigate(routes.supplierPortalDataEntryPlanning);
    }
  };

  const handleReturnToListClick = () => {
    if (data?.status === QUESTIONNAIRE_STATUSES.FINALIZED || data?.physicalRiskLocations?.length === 0 || isPreview) {
      executeNavigation();
    } else {
      setSubmitAndReturnToList(true);
    }
  };

  const handleNavigationToList = () => {
    setSubmitAndReturnToList(false);
    executeNavigation();
  };

  const handleErrors = (err) => {
    let errorMessage = err.message;
    if (err.response?.status === 404) {
      return;
    } else if (
      err.response?.status === 409 &&
      err.response?.data?.parameters?.resourceName === "SupplierQuestionnairePhysicalRiskLocation"
    ) {
      errorMessage = t("duplicatedAddressesMsg");
    }
    showErrorToast(toast, errorMessage);
  };

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, t("newLocationCreated"));
      queryClient.invalidateQueries(supplierPhysicalRiskQuestionnaireKeys.supplierPhysicalRiskQuestionnaireById());
      reset();
    },
    onError: (err) => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
  };

  const { mutate: createNewLocation, isLoading: isLoadingOnCreate } = useCreateNewLocation(queryConfig);

  useEffect(() => {
    if (isLoading || isLoadingOnCreate) {
      dispatch(showAppSpinner());
    }
  }, [isLoading, isLoadingOnCreate, dispatch]);

  const onSubmit = (submitData) => {
    createNewLocation({
      physicalRiskId: data.id,
      data: {
        location: submitData.newLocation,
        supplierName: submitData.supplierName,
        tier: submitData.tier,
      },
    });
  };

  if ((error && error.response.status === 404) || (!isLoading && !isFetching && typeof data === "undefined")) {
    return (
      <Flex blockSize="full">
        <Center flex="1">
          <Box textAlign="center">
            {error && error.response.status === 404 ? (
              <>
                <Tag size="lg">{globalTranslation("noAvailableQuestionnaire")}</Tag>
                <Box>
                  <ReturnToList onClickHandler={() => navigate(getSupplierQuestionnairesRoute(userPermissions))} />
                </Box>
              </>
            ) : (
              <Tag size="lg">{globalTranslation("somethingWentWrong")}</Tag>
            )}
          </Box>
        </Center>
      </Flex>
    );
  } else {
    return data !== undefined ? (
      <Page
        title={t("page.title")}
        subTitle={t("page.subTitle", {
          companyName: data && data.organization ? data.organization : t("page.yourCompany"),
        })}
      >
        <Card>
          <AssigneeHeader
            assignee={
              assignee && data && assignee?.name?.toLowerCase() !== data.assigneeName?.toLowerCase()
                ? { name: data.assigneeName }
                : assignee
            }
            period={data.period}
            customComponent={
              <ReturnToList
                onClickHandler={handleReturnToListClick}
                title={
                  data.status !== QUESTIONNAIRE_STATUSES.FINALIZED ? globalTranslation("returnToListTooltip") : null
                }
              />
            }
          />
          {(data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) &&
          !isPreview ? (
            <Box mb={10}>
              <chakra.form id="addLocation" onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="4" marginBlockStart="5" alignItems="baseline">
                  <FormInput
                    placeholder={t("inputs.newLocationPlaceholder")}
                    errors={errors}
                    {...register("newLocation", { required: t("inputs.newLocationRequired") })}
                  />
                  <FormInput
                    placeholder={t("inputs.supplierNamePlaceholder")}
                    errors={errors}
                    {...register("supplierName", { required: t("inputs.supplierNameRequired") })}
                  />
                  <FormSelect errors={errors} {...register("tier", { required: t("inputs.tierRequired") })}>
                    <option key="MY_COMPANY" value="TIER1">
                      My Company
                    </option>
                    <option key="TIER2" value="TIER2">
                      Tier 1
                    </option>
                    <option key="TIER3" value="TIER3">
                      Tier 2
                    </option>
                    <option key="TIER4" value="TIER4">
                      Tier 3
                    </option>
                  </FormSelect>
                  <Button colorScheme="customScheme" width="32" type="submit" title={t("buttons.addLocation")}>
                    {t("buttons.addLocation")}
                  </Button>
                </Flex>
              </chakra.form>
            </Box>
          ) : null}
          {data.physicalRiskLocations && data.physicalRiskLocations.length > 0 ? (
            (data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) &&
            !isPreview ? (
              <LocationQuestionnaire
                data={data}
                returnToListRequested={submitAndReturnToList}
                returnToList={handleNavigationToList}
              />
            ) : (
              <NavigationTabs
                elements={mapLocationsModalData(data.physicalRiskLocations)}
                tabTitle={"Location"}
                subTitleAttribute={"name"}
              />
            )
          ) : (
            <Heading size="md" marginBlock="1">
              {t("page.missingLocations")}
            </Heading>
          )}
        </Card>
      </Page>
    ) : null;
  }
}

export default WithAuthorization(SupplierPhysicalRiskQuestionnaire);
